<script lang="ts">
  // LineChart — SVG line chart à la Grafana/Azure: rolling series,
  // crosshair on hover showing value + timestamp, x-axis time labels.
  //
  // Props:
  //   series   – HistoryPoint[] to plot (see lib/ws.ts buffers)
  //   max      – y-axis ceiling (default: auto from data)
  //   unit     – suffix for values ("%", "MiB", …)
  //   label    – accessible name
  //   height   – svg height in px

  import type { HistoryPoint } from "../lib/ws";

  export let series: HistoryPoint[] = [];
  export let max: number | null = null;
  export let unit = "";
  export let label = "metric chart";
  export let height = 72;

  const W = 300;
  const PAD_T = 6;
  const PAD_B = 4;

  $: points = series.slice(-180);
  $: hasData = points.length >= 2;
  $: yMax = Math.max(max ?? 0, ...points.map((p) => p.v), 1);
  $: current = points.length > 0 ? points[points.length - 1]! : null;
  $: firstTs = points[0]?.ts ?? 0;
  $: lastTs = current?.ts ?? 0;

  // --- geometry -----------------------------------------------------------
  function px(i: number): number {
    return (i / Math.max(1, points.length - 1)) * W;
  }
  function py(v: number): number {
    return PAD_T + (1 - v / yMax) * (height - PAD_T - PAD_B);
  }
  $: path = (() => {
    if (!hasData) return "";
    const step = W / (points.length - 1);
    return points
      .map((p, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${py(p.v).toFixed(1)}`)
      .join(" ");
  })();
  $: areaPath = path ? `${path} L${W},${height} L0,${height} Z` : "";

  // --- hover state ----------------------------------------------------------
  let hoverX = 0; // svg coords
  let hovering = false;
  $: hoverIndex = hovering ? nearestIndex(hoverX) : -1;
  $: hoverPoint = hoverIndex >= 0 && hoverIndex < points.length ? points[hoverIndex] : null;
  // Tooltip anchored to the hovered point, flipped near the right edge.
  $: tipLeft = hoverPoint
    ? Math.min(Math.max(px(hoverIndex) / W, 0), 1) * 100
    : 0;
  $: flipTip = tipLeft > 70;

  function nearestIndex(svgX: number): number {
    if (points.length === 0) return -1;
    const frac = Math.min(1, Math.max(0, svgX / W));
    return Math.round(frac * (points.length - 1));
  }

  function onMove(e: MouseEvent): void {
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    hoverX = ((e.clientX - rect.left) / rect.width) * W;
    hovering = true;
  }
  function onLeave(): void {
    hovering = false;
  }

  // --- formatting -----------------------------------------------------------
  function fmtTime(ts: number): string {
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }
  function fmtValue(v: number | undefined): string {
    if (v === undefined) return "—";
    return v >= 100 ? v.toFixed(0) : v.toFixed(1);
  }
</script>

<div class="line-chart" role="img" aria-label={label}>
  {#if hasData}
    <svg
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      on:mousemove={onMove}
      on:mouseleave={onLeave}
    >
      <!-- horizontal gridlines: 25/50/75% -->
      <line class="grid" x1="0" y1={PAD_T + (height - PAD_T - PAD_B) * 0.25} x2={W} y2={PAD_T + (height - PAD_T - PAD_B) * 0.25} />
      <line class="grid" x1="0" y1={PAD_T + (height - PAD_T - PAD_B) * 0.5} x2={W} y2={PAD_T + (height - PAD_T - PAD_B) * 0.5} />
      <line class="grid" x1="0" y1={PAD_T + (height - PAD_T - PAD_B) * 0.75} x2={W} y2={PAD_T + (height - PAD_T - PAD_B) * 0.75} />
      <path class="area" d={areaPath} />
      <path class="line" d={path} />
      {#if hoverPoint}
        <line class="crosshair" x1={px(hoverIndex)} y1="0" x2={px(hoverIndex)} y2={height} />
        <circle class="dot" cx={px(hoverIndex)} cy={py(hoverPoint.v)} r="3" />
      {/if}
    </svg>

    {#if hoverPoint}
      <div
        class="tooltip"
        class:flip={flipTip}
        style={`left: ${tipLeft}%;`}
        data-testid="chart-tooltip"
      >
        <span class="tooltip__value">{fmtValue(hoverPoint.v)}{unit}</span>
        <span class="tooltip__time">{fmtTime(hoverPoint.ts)}</span>
      </div>
    {:else}
      <span class="line-chart__current mono">{fmtValue(current?.v)}{unit}</span>
    {/if}

    <div class="xaxis">
      <span>{fmtTime(firstTs).slice(0, 5)}</span>
      <span>{fmtTime((firstTs + lastTs) / 2).slice(0, 5)}</span>
      <span>{fmtTime(lastTs).slice(0, 5)}</span>
    </div>
  {:else}
    <div class="line-chart__empty-wrap">
      <span class="line-chart__empty">collecting samples…</span>
    </div>
  {/if}
</div>

<style>
  .line-chart {
    position: relative;
    width: 100%;
  }
  svg {
    display: block;
    width: 100%;
    height: auto;
    cursor: crosshair;
  }
  .grid {
    stroke: var(--border);
    stroke-width: 1;
    stroke-dasharray: 3 4;
  }
  .line {
    fill: none;
    stroke: var(--accent-cyan);
    stroke-width: 1.6;
    vector-effect: non-scaling-stroke;
  }
  .area {
    fill: var(--accent-bg);
    stroke: none;
  }
  .crosshair {
    stroke: var(--text-dim);
    stroke-width: 1;
    vector-effect: non-scaling-stroke;
  }
  .dot {
    fill: var(--accent-cyan);
    stroke: var(--surface);
    stroke-width: 1.5;
  }
  .tooltip {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    background: var(--surface-3);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: 2px var(--space-2);
    display: flex;
    flex-direction: column;
    align-items: center;
    pointer-events: none;
    white-space: nowrap;
    z-index: 3;
    box-shadow: var(--shadow-card, none);
  }
  /* Flip so it never clips against the right edge. */
  .tooltip.flip {
    transform: translateX(-100%);
  }
  .tooltip__value {
    font: var(--type-mono-lg);
    color: var(--text-strong);
    font-weight: 700;
  }
  .tooltip__time {
    font: var(--type-mono-md);
    color: var(--text-mid);
  }
  .line-chart__current {
    position: absolute;
    top: 2px;
    right: 4px;
    font: var(--type-mono-md);
    color: var(--text-strong);
    background: var(--surface);
    padding: 0 var(--space-1);
    border-radius: var(--radius-sm);
    pointer-events: none;
  }
  .xaxis {
    display: flex;
    justify-content: space-between;
    font: var(--type-label);
    color: var(--text-dim);
    margin-top: 1px;
  }
  .line-chart__empty-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
  }
  .line-chart__empty {
    font: var(--type-body-sm);
    color: var(--text-dim);
  }
</style>
